import { ReactNode, useState } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Users,
  UserCheck,
  GraduationCap,
  BookOpen,
  Database,
  Layers,
  FileText,
  ArrowRight,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  DashboardStats,
  RecentActivity,
  SystemHealth,
} from '@/lib/api/services/dashboard-service-new'
import { useDashboard } from '@/hooks/useDashboard'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ActivityLog, ActivityLogItem } from './ActivityLog'
import { ActivityLogList } from './ActivityLogList'
import { useTranslation } from 'react-i18next'
// import { DatabaseOverviewTour } from '@/tour/admin/databaseOverviewTour'

interface DatabaseCardProps {
  title: string
  count: number | string
  icon: ReactNode
  description?: string
  color?: 'blue' | 'green' | 'purple' | 'amber' | 'rose' | 'indigo' | 'slate'
  onClick?: () => void
}

const colorVariants = {
  blue: 'bg-blue-50 border-blue-200 text-blue-700',
  green: 'bg-green-50 border-green-200 text-green-700',
  purple: 'bg-purple-50 border-purple-200 text-purple-700',
  amber: 'bg-amber-50 border-amber-200 text-amber-700',
  rose: 'bg-rose-50 border-rose-200 text-rose-700',
  indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
  slate: 'bg-slate-50 border-slate-200 text-slate-700',
}

const iconColorVariants = {
  blue: 'bg-blue-100 text-blue-700',
  green: 'bg-green-100 text-green-700',
  purple: 'bg-purple-100 text-purple-700',
  amber: 'bg-amber-100 text-amber-700',
  rose: 'bg-rose-100 text-rose-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  slate: 'bg-slate-100 text-slate-700',
}

const DatabaseCard = ({
  title,
  count,
  icon,
  description,
  color = 'blue',
  onClick,
}: DatabaseCardProps) => {
  const { t } = useTranslation('shared')
  
  return (
    <Card
      className={cn(
        'overflow-hidden border transition-all duration-300 hover:shadow-md',
        colorVariants[color],
        onClick && 'cursor-pointer hover:scale-[1.02]'
      )}
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <CardTitle className="text-lg font-semibold">{title}</CardTitle>
          <div className={cn('p-2 rounded-full', iconColorVariants[color])}>
            {icon}
          </div>
        </div>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{count}</div>
      </CardContent>
      {onClick && (
        <CardFooter className="pt-0">
          <Button
            variant="ghost"
            className="p-0 h-auto text-sm font-medium hover:bg-transparent hover:underline"
          >
            {t('databaseOverview.cards.viewDetails')} <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        </CardFooter>
      )}
    </Card>
  )
}

interface RecentItemProps {
  activity: RecentActivity
}

const RecentItem = ({ activity }: RecentItemProps) => {
  // Convert RecentActivity to ActivityLogItem
  const activityLogItem: ActivityLogItem = {
    id: activity.id,
    type: activity.type,
    action: activity.action.toLowerCase() as 'create' | 'update' | 'delete',
    entityId: activity.entityId,
    entityName: activity.entityName,
    timestamp: activity.timestamp,
    details: activity.details,
    performedBy: activity.performedBy,
  }

  return <ActivityLog activity={activityLogItem} />
}

interface DatabaseOverviewProps {
  role: 'admin' | 'super_admin' | 'teacher'
  onCardClick?: (section: string) => void
}

interface DashboardHookReturn {
  stats: DashboardStats
  activities: RecentActivity[]
  systemHealth: SystemHealth
  teacherStats?: any
  isLoading: boolean
  error: string | null
}

export function DatabaseOverview({ role, onCardClick }: DatabaseOverviewProps) {
  const [isActivitiesModalOpen, setIsActivitiesModalOpen] = useState(false)
  const [allActivities, setAllActivities] = useState<RecentActivity[]>([])
  const [isLoadingAllActivities, setIsLoadingAllActivities] = useState(false)
  const { t } = useTranslation('shared')

  // Default values for the dashboard data
  const defaultDashboardData: DashboardHookReturn = {
    stats: {} as DashboardStats,
    activities: [],
    systemHealth: {
      storage: { used: 0, total: 1, unit: 'GB' },
      records: { total: 0, students: 0, teachers: 0, parents: 0, other: 0 },
      backups: {
        last: new Date().toISOString(),
        next: new Date().toISOString(),
        status: 'ok',
      },
      performance: { responseTime: 0, unit: 'ms', status: 'good' },
      scheduledTasks: [],
    },
    isLoading: false,
    error: null,
  }

  // Use the hook and provide proper type
  const dashboardData = useDashboard(role) || defaultDashboardData

  const {
    stats,
    activities: recentActivities,
    systemHealth,
    isLoading,
    error,
    teacherStats,
  } = dashboardData

  const handleViewAllActivities = async () => {
    setIsActivitiesModalOpen(true)
    setIsLoadingAllActivities(true)

    try {
      const response = await fetch('/api/database/activities', {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Send HTTP-only cookies
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      setAllActivities(data)
    } catch (error) {
      // console.error('Error fetching all activities:', error)
      setAllActivities(recentActivities)
    } finally {
      setIsLoadingAllActivities(false)
    }
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-96 text-red-500">
        <p>Error loading dashboard data</p>
      </div>
    )
  }

  return (
    <>
      <div data-tour="database-overview" className="space-y-8">
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold tracking-tight">
              {t('databaseOverview.title')}
            </h2>
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="flex items-center gap-1">
                <Database className="h-3 w-3" />
                {t('databaseOverview.lastUpdated')} {new Date().toLocaleTimeString()}
              </Badge>
            </div>
          </div>

          <div data-tour="stats-cards" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {role === 'admin' && (
              <>
                <DatabaseCard
                  title={t('databaseOverview.cards.students')}
                  count={stats?.students?.total || 0}
                  icon={<Users className="h-5 w-5" />}
                  color="blue"
                  onClick={() => onCardClick?.('students')}
                />

                <DatabaseCard
                  title={t('databaseOverview.cards.teachers')}
                  count={stats?.teachers?.total || 0}
                  icon={<GraduationCap className="h-5 w-5" />}
                  color="green"
                  onClick={() => onCardClick?.('teachers')}
                />

                <DatabaseCard
                  title={t('databaseOverview.cards.parents')}
                  count={stats?.parents?.total || 0}
                  icon={<UserCheck className="h-5 w-5" />}
                  color="purple"
                  onClick={() => onCardClick?.('parents')}
                />

                <DatabaseCard
                  title={t('databaseOverview.cards.classes')}
                  count={stats?.classes?.total || 0}
                  icon={<BookOpen className="h-5 w-5" />}
                  color="amber"
                  onClick={() => onCardClick?.('classes')}
                />

                <DatabaseCard
                  title={t('databaseOverview.cards.subjects')}
                  count={stats?.subjects?.total || 0}
                  icon={<Database className="h-5 w-5" />}
                  color="indigo"
                  onClick={() => onCardClick?.('subjects')}
                />

                <DatabaseCard
                  title={t('databaseOverview.cards.classrooms')}
                  count={stats?.classrooms?.total || 0}
                  icon={<Layers className="h-5 w-5" />}
                  color="rose"
                  onClick={() => onCardClick?.('classrooms')}
                />
              </>
            )}

            {role === 'super_admin' && (
              <>
                <DatabaseCard
                  title={t('databaseOverview.cards.admins')}
                  count={(stats as DashboardStats).admins.total}
                  icon={<UserCheck className="h-5 w-5" />}
                  color="blue"
                  onClick={() => onCardClick?.('AdminManagement')}
                />

                <DatabaseCard
                  title={t('databaseOverview.cards.students')}
                  count={(stats as DashboardStats).students.total}
                  icon={<Users className="h-5 w-5" />}
                  color="blue"
                  onClick={() => onCardClick?.('StudentManagement')}
                />

                <DatabaseCard
                  title={t('databaseOverview.cards.teachers')}
                  count={(stats as DashboardStats).teachers.total}
                  icon={<UserCheck className="h-5 w-5" />}
                  color="green"
                  onClick={() => onCardClick?.('TeacherManagement')}
                />

                <DatabaseCard
                  title={t('databaseOverview.cards.parents')}
                  count={(stats as DashboardStats).parents.total}
                  icon={<Users className="h-5 w-5" />}
                  color="purple"
                  onClick={() => onCardClick?.('ParentManagement')}
                />

                <DatabaseCard
                  title={t('databaseOverview.cards.classes')}
                  count={(stats as DashboardStats).classes.total}
                  icon={<GraduationCap className="h-5 w-5" />}
                  color="amber"
                  onClick={() => onCardClick?.('ClassManagement')}
                />

                <DatabaseCard
                  title={t('databaseOverview.cards.subjects')}
                  count={(stats as DashboardStats).subjects.total}
                  icon={<BookOpen className="h-5 w-5" />}
                  color="indigo"
                  onClick={() => onCardClick?.('SubjectManagement')}
                />

                <DatabaseCard
                  title={t('databaseOverview.cards.classrooms')}
                  count={(stats as DashboardStats).classrooms.total}
                  icon={<Layers className="h-5 w-5" />}
                  color="rose"
                  onClick={() => onCardClick?.('ClassroomManagement')}
                />
              </>
            )}

            {role === 'teacher' && (
              <>
                <DatabaseCard
                  title={t('databaseOverview.cards.myClasses')}
                  count={(teacherStats as any)?.classes || 0}
                  icon={<GraduationCap className="h-5 w-5" />}
                  color="blue"
                  onClick={() => onCardClick?.('MyClasses')}
                />

                <DatabaseCard
                  title={t('databaseOverview.cards.myStudents')}
                  count={(teacherStats as any)?.students || 0}
                  icon={<Users className="h-5 w-5" />}
                  color="green"
                  onClick={() => onCardClick?.('MyStudents')}
                />

                <DatabaseCard
                  title={t('databaseOverview.cards.subjects')}
                  count={(teacherStats as any)?.subjects || 0}
                  icon={<BookOpen className="h-5 w-5" />}
                  color="purple"
                  onClick={() => onCardClick?.('MySubjects')}
                />

                <DatabaseCard
                  title={t('databaseOverview.cards.assignments')}
                  count={(teacherStats as any)?.assignments || 0}
                  icon={<FileText className="h-5 w-5" />}
                  color="amber"
                  onClick={() => onCardClick?.('Assignments')}
                />
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div data-tour="recent-activities" className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">{t('databaseOverview.recentActivities.title')}</CardTitle>
                <CardDescription>{t('databaseOverview.recentActivities.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {recentActivities.slice(0, 5).map((activity) => (
                  <RecentItem key={activity.id} activity={activity} />
                ))}
              </CardContent>
              <CardFooter>
                <Button
                  variant="ghost"
                  className="w-full justify-center"
                  onClick={handleViewAllActivities}
                >
                  {t('databaseOverview.recentActivities.viewAll')}
                </Button>
              </CardFooter>
            </Card>
          </div>

          <div data-tour="system-health" className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">{t('databaseOverview.systemHealth.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Storage Usage */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t('databaseOverview.systemHealth.storage')}</span>
                    <span>{systemHealth?.storage?.used || 0}/{systemHealth?.storage?.total || 1} {systemHealth?.storage?.unit || 'GB'}</span>
                  </div>
                  <Progress value={((systemHealth?.storage?.used || 0) / (systemHealth?.storage?.total || 1)) * 100} />
                </div>

                {/* Records Count */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t('databaseOverview.systemHealth.records')}</span>
                    <span>{systemHealth?.records?.total || 0}</span>
                  </div>
                </div>

                {/* Backup Status */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span>{t('databaseOverview.systemHealth.lastBackup')}</span>
                    <span>{new Date(systemHealth?.backups?.last || Date.now()).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span>{t('databaseOverview.systemHealth.nextBackup')}</span>
                    <span>{new Date(systemHealth?.backups?.next || Date.now()).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span>{t('databaseOverview.systemHealth.systemStatus')}</span>
                    <Badge variant={systemHealth?.backups?.status === 'ok' ? 'success' : 'destructive'}>
                      {systemHealth?.backups?.status === 'ok' ? 'Healthy' : 'Warning'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        

        {/* Activities Modal */}
        <Dialog open={isActivitiesModalOpen} onOpenChange={setIsActivitiesModalOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('databaseOverview.allActivities.title')}</DialogTitle>
            </DialogHeader>
            {isLoadingAllActivities ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              <ActivityLogList 
                activities={allActivities.map(activity => ({
                  ...activity,
                  action: activity.action.toLowerCase() as 'create' | 'update' | 'delete'
                }))} 
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
      {/* <DatabaseOverviewTour /> */}
    </>
  )
}
